import { serialize, deserialize } from '@campai/lib-common/utils/misc'

export type KeyValueStorageImplementation = {
    get: (key: string) => Promise<string | null>
    set: (key: string, value: string) => Promise<void>
    remove: (key: string) => Promise<void>
    clear: () => Promise<void>
    listKeys?: () => Promise<string[]>
}

export class KeyValueStorage {
    private implementation: KeyValueStorageImplementation

    constructor(storageImplementation: KeyValueStorageImplementation) {
        this.implementation = storageImplementation

        if (!this.implementation) {
            throw new Error(`Storage implementation ${storageImplementation} not found`)
        }
    }

    async get<T>(key: string): Promise<T | undefined> {
        if (!key) {
            throw new Error('Key is required')
        }

        const raw = await this.implementation.get(key)
        if (raw === null) {
            return undefined
        }

        try {
            return deserialize(raw) as T
        } catch (e) {
            console.error(e)
            return undefined
        }
    }

    async set<T>(key: string, value: T) {
        if (!key) {
            throw new Error('Key is required')
        }

        const res = serialize(value)

        if (typeof res === 'string') {
            await this.implementation.set(key, res)
        } else {
            throw new Error('Failed to serialize value')
        }
    }

    async remove(keyOrWildcard: string) {
        if (!keyOrWildcard) {
            throw new Error('Key is required')
        }

        if (!keyOrWildcard.includes('*')) {
            await this.implementation.remove(keyOrWildcard)
            return
        }

        const escaped = keyOrWildcard.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const regex = new RegExp(`^${escaped.replace(/\\\*/g, '.*')}$`)

        const matches = (key: string) => regex.test(key)
        const keysToRemove = (await this.listKeys()).filter(matches)

        await Promise.all(keysToRemove.map((key) => this.implementation.remove(key)))
    }

    async clear() {
        await this.implementation.clear()
    }

    private async listKeys() {
        if (this.implementation.listKeys) {
            return this.implementation.listKeys()
        }

        const keys: string[] = []

        for (let index = 0; index < window.localStorage.length; index += 1) {
            const key = window.localStorage.key(index)
            if (key) {
                keys.push(key)
            }
        }

        return keys
    }
}
